// Snapshot AGREGADO do painel BX para o chat IA (aba Miguel).
//
// Resume o estado do estoque/compras em números — NUNCA linha bruta do banco
// (mesma postura do ai-insights). É o "contexto" que vai junto da pergunta
// para a Edge Function ai-chat. Top-N capado para controlar tokens.

import type { ProductRow, PurchaseOrderWithItems } from '../types'
import type { CompanyBudget } from '../hooks/useCompanyBudget'

const TOP_COMPRAR = 15
const TOP_FORNECEDORES = 30

export interface PanelSnapshot {
  gerado_em: string
  estoque: {
    total_skus: number
    em_risco: number
    encalhe: number
    zerados: number
    cobertura_media_dias: number
    valor_estoque_custo: number
  }
  curva_abc: { A: number; B: number; C: number; sem_curva: number }
  prioridades: Record<string, number>
  comprar_agora: Array<{
    sku: string
    nome: string
    qty_sugerida: number
    cobertura_dias: number | null
    curva: string
    prioridade: string
    em_risco: boolean
  }>
  fornecedores: Array<{ nome: string; lead_time_dias: number | null; compra_minima: number | null }>
  pedidos_abertos: {
    pendente: { qtd: number; valor: number }
    enviado: { qtd: number; valor: number }
  }
  cap_mensal: { teto: number; dia_corte: number }
}

// Urgência decrescente — usada para ordenar "o que comprar primeiro".
const PRIORITY_RANK: Record<string, number> = { Zerado: 0, Baixo: 1, Comprar: 2 }

const finite = (n: number) => (Number.isFinite(n) ? n : null)

interface SupplierLite { name?: string; lead_time_days?: number | null; min_order?: number | null }

export function buildPanelSnapshot(
  rows: ProductRow[],
  orders: PurchaseOrderWithItems[],
  budget: CompanyBudget,
): PanelSnapshot {
  const totalSkus = rows.length
  let emRisco = 0
  let encalhe = 0
  let zerados = 0
  let coberturaSoma = 0
  let coberturaN = 0
  let valorEstoque = 0
  const abc = { A: 0, B: 0, C: 0, sem_curva: 0 }
  const prioridades: Record<string, number> = {}

  for (const r of rows) {
    if (r.em_risco) emRisco++
    if (r.encalhe) encalhe++
    if (r.current_stock <= 0) zerados++
    const cov = finite(r.coverage_days)
    if (cov != null) { coberturaSoma += cov; coberturaN++ }
    if (Number.isFinite(r.cost)) valorEstoque += r.current_stock * r.cost
    if (r.abc_curve === 'A' || r.abc_curve === 'B' || r.abc_curve === 'C') abc[r.abc_curve]++
    else abc.sem_curva++
    prioridades[r.priority] = (prioridades[r.priority] ?? 0) + 1
  }

  // O que comprar primeiro: sugestão > 0, ordenado por urgência e cobertura.
  const comprar_agora = rows
    .filter(r => r.total_suggestion > 0)
    .sort((a, b) => {
      const ra = PRIORITY_RANK[a.priority] ?? 9
      const rb = PRIORITY_RANK[b.priority] ?? 9
      if (ra !== rb) return ra - rb
      return (finite(a.coverage_days) ?? 9999) - (finite(b.coverage_days) ?? 9999)
    })
    .slice(0, TOP_COMPRAR)
    .map(r => ({
      sku: r.sku,
      nome: r.name,
      qty_sugerida: r.total_suggestion,
      cobertura_dias: finite(r.coverage_days),
      curva: r.abc_curve ?? '?',
      prioridade: r.priority,
      em_risco: r.em_risco,
    }))

  // Fornecedores distintos (a partir dos itens ativos).
  const supMap = new Map<string, { nome: string; lead_time_dias: number | null; compra_minima: number | null }>()
  for (const r of rows) {
    const s = r.suppliers as SupplierLite | null
    if (!s?.name) continue
    if (!supMap.has(s.name)) {
      supMap.set(s.name, {
        nome: s.name,
        lead_time_dias: s.lead_time_days ?? null,
        compra_minima: s.min_order ?? null,
      })
    }
  }
  const fornecedores = [...supMap.values()]
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
    .slice(0, TOP_FORNECEDORES)

  // Pedidos em aberto por status.
  const pedidos_abertos = {
    pendente: { qtd: 0, valor: 0 },
    enviado: { qtd: 0, valor: 0 },
  }
  for (const o of orders) {
    if (o.status === 'Pendente') { pedidos_abertos.pendente.qtd++; pedidos_abertos.pendente.valor += o.total_value }
    else if (o.status === 'Enviado') { pedidos_abertos.enviado.qtd++; pedidos_abertos.enviado.valor += o.total_value }
  }

  return {
    gerado_em: new Date().toISOString(),
    estoque: {
      total_skus: totalSkus,
      em_risco: emRisco,
      encalhe,
      zerados,
      cobertura_media_dias: coberturaN > 0 ? Math.round(coberturaSoma / coberturaN) : 0,
      valor_estoque_custo: Math.round(valorEstoque),
    },
    curva_abc: abc,
    prioridades,
    comprar_agora,
    fornecedores,
    pedidos_abertos,
    cap_mensal: { teto: budget.purchase_budget_monthly, dia_corte: budget.purchase_cutoff_day },
  }
}
